import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "../styles/SelectNewCyclists.css";

const SelectCyclists = ({
  selectedCyclists,
  numberPlayerSelected,
  setNumberPlayerSelected,
  budget,
  setBudget,
  selectedNewCyclists,
  setSelectedNewCyclists,
  cyclists,
  prevStep,
  nextStep,
}) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [pool, setPool] = useState([]);
  const [order, setOrder] = useState("value");

  useEffect(() => {
    const lockedIds = new Set((selectedCyclists || []).map((c) => c?.cyclistId).filter((id) => id != null));
    const filtered = (cyclists || []).filter((c) => !lockedIds.has(c?.cyclistId));
    setPool(filtered);
  }, [cyclists, selectedCyclists]);

  const selectedCount = useMemo(() => (selectedNewCyclists || []).length, [selectedNewCyclists]);

  const isPicked = (id) => (selectedNewCyclists || []).some((c) => c?.cyclistId === id);

  const sortList = (kind, list) => {
    const next = [...(list || [])];
    switch (kind) {
      case "nation":
        next.sort((a, b) => (a?.nationality || "").localeCompare(b?.nationality || ""));
        break;
      case "team":
        next.sort((a, b) => (a?.team || "").localeCompare(b?.team || ""));
        break;
      case "value":
      default:
        next.sort((a, b) => (b?.finalValue || 0) - (a?.finalValue || 0));
        break;
    }
    return next;
  };

  const visibleCyclists = useMemo(() => {
    const term = query.trim().toLowerCase();
    let list = [...(pool || [])];

    if (term) {
      list = list.filter((c) => (c?.name || "").toLowerCase().startsWith(term));
    }

    return sortList(order, list);
  }, [pool, query, order]);

  const togglePick = (cyclist) => {
    const id = cyclist?.cyclistId;
    if (id == null) return;

    if (isPicked(id)) {
      setSelectedNewCyclists((selectedNewCyclists || []).filter((c) => c?.cyclistId !== id));
      setBudget(budget + (cyclist?.finalValue || 0));
      setNumberPlayerSelected(numberPlayerSelected + 1);
      return;
    }

    if (budget >= (cyclist?.finalValue || 0)) {
      setSelectedNewCyclists([...(selectedNewCyclists || []), cyclist]);
      setBudget(budget - (cyclist?.finalValue || 0));
      setNumberPlayerSelected(numberPlayerSelected - 1);
    }
  };

  const isCtaDisabled = numberPlayerSelected !== 0;

  return (
    <main className="select-new-cyclists">
      <div className="select-new-cyclists-antiscroll">
        <div className="select-new-cyclists-shadow-container">
          <div className="select-new-cyclists-title">
            <h1>{t("swapNewCyclists.pageTitle")}</h1>
            <div className="select-new-cyclists-shadow-mask-right"></div>
            <div className="select-new-cyclists-shadow-mask-bottom"></div>
          </div>
        </div>

        <div className="select-new-cyclists-top-container">
          <div className="select-new-cyclists-rules">
            <p>{t("swapNewCyclists.pageHint")}</p>
          </div>

          <div className="select-new-cyclists-header-pannel">
            <div className="select-new-cyclists-header-pannel-content">
              <p>
                {t("swapNewCyclists.selectedCount")} <span>{selectedCount}</span>
              </p>
            </div>

            <div className="select-new-cyclists-header-pannel-content">
              <p>
                {t("swapNewCyclists.budgetLeft")} <strong>{budget}M</strong>
              </p>
            </div>

            <div className="select-new-cyclists-change-step">
              <button onClick={prevStep}>{t("swapNewCyclists.backBtn")}</button>
              <button onClick={nextStep} disabled={isCtaDisabled}>
                {t("swapNewCyclists.submitBtn")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="select-new-cyclists-pannel-view">
        <div className="select-new-cyclists-choice">
          <div className="select-new-cyclists-controls">
            <div className="select-new-cyclists-sort-control">
              <label>{t("swapNewCyclists.sortLabel")}</label>
              <select value={order} onChange={(e) => setOrder(e.target.value)}>
                <option value="value">{t("swapNewCyclists.sortValue")}</option>
                <option value="nation">{t("swapNewCyclists.sortNation")}</option>
                <option value="team">{t("swapNewCyclists.sortTeam")}</option>
              </select>
            </div>

            <div className="select-new-cyclists-search-control">
              <img
                src={`${process.env.PUBLIC_URL}/icons/loupe.png`}
                alt={t("swapNewCyclists.searchIconAlt")}
                width="22"
                height="22"
                className="select-new-cyclists-search-icon"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("swapNewCyclists.searchPlaceholder")}
              />
            </div>
          </div>

          <div className="select-new-cyclists-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("swapNewCyclists.cols.rider")}</th>
                  <th>{t("swapNewCyclists.cols.squad")}</th>
                  <th>{t("swapNewCyclists.cols.country")}</th>
                  <th>{t("swapNewCyclists.cols.worth")}</th>
                  <th>{t("swapNewCyclists.cols.role")}</th>
                </tr>
              </thead>

              <tbody>
                {visibleCyclists.map((cyclist) => (
                  <tr
                    key={cyclist.cyclistId}
                    onClick={() => togglePick(cyclist)}
                    className={isPicked(cyclist.cyclistId) ? "select-new-cyclists-table-selected-row" : ""}
                  >
                    <td>
                      <div className="select-new-cyclists-name-info">
                        <img
                          className="select-new-cyclists-flag-icon"
                          src={`${process.env.PUBLIC_URL}/flags/${(cyclist.nationality || "")
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "")
                            .replace(/ /g, "_")
                            .toLowerCase()}.png`}
                          alt={t("swapNewCyclists.flagAlt", { country: cyclist.nationality })}
                          width="20"
                          height="20"
                        />
                        <span>{cyclist.name}</span>
                      </div>
                    </td>
                    <td>{cyclist.team}</td>
                    <td>{cyclist.nationality}</td>
                    <td>{cyclist.finalValue}</td>
                    <td>{cyclist.speciality}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="select-new-cyclists-choice-view">
          <div className="select-new-cyclists-line-container">
            {(selectedNewCyclists || []).map((cyclist) => (
              <div className="select-new-cyclists-selected-cell" key={cyclist.cyclistId}>
                <img
                  src={`${process.env.PUBLIC_URL}/icons/cancel.png`}
                  className="select-new-cyclists-cancel-icon"
                  alt={t("swapNewCyclists.removeAlt")}
                  width="17"
                  height="17"
                  onClick={() => togglePick(cyclist)}
                />
                <div className="select-new-cyclists-selected-info">
                  <h4>
                    <img
                      className="select-new-cyclists-flag-icon"
                      src={`${process.env.PUBLIC_URL}/flags/${(cyclist.nationality || "")
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .replace(/ /g, "_")
                        .toLowerCase()}.png`}
                      alt={t("swapNewCyclists.flagAlt", { country: cyclist.nationality })}
                      width="20"
                      height="20"
                    />
                    {cyclist.name}
                  </h4>
                  <p>{cyclist.team}</p>
                  <p>
                    {t("swapNewCyclists.worthLine")} <strong>{cyclist.finalValue}</strong>
                  </p>
                  <div
                    style={{
                      width: "100%",
                      height: "15px",
                      backgroundColor: cyclist.teamColor,
                      marginTop: "35px",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

export default SelectCyclists;
